import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { FormBuilder } from '@angular/forms';
import { Observable, Subscription, filter } from 'rxjs';
import moment from 'moment';
import { LANGUAGES } from 'src/app/conf/languages';
import { Lang } from 'src/app/model/lang';
import { Permission, PermittedCommand } from 'src/app/model/permission';
import * as PermissionActions from 'src/app/store/permissions/permissions.actions';
import { selectPermission, selectPermittedCommands } from 'src/app/store/permissions/permissions.selector';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { OverlayPanel } from 'primeng/overlaypanel';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { EDeviceModel, isMtmacModel, isMultiIo, isRbe24230 } from 'src/app/conf/device-models';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { Site } from 'src/app/model/site';
import { Device } from 'src/app/model/device';
import { convertDbStandartIcon, convertOldIcon, DeviceIcon } from 'src/app/conf/device-icons';

@Component({
	templateUrl: './permission-info.dialog.component.html',
})

export class PermissionInfoDialogComponent implements OnInit, AfterViewInit, OnDestroy {
	permittedCommand$: Observable<PermittedCommand[] | undefined>;
	permittedCommandSub: Subscription | undefined;
	permission$: Observable<Permission | undefined>;
	permissionSub: Subscription | undefined;
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;

	permission: Permission;
	permittedCommands: PermittedCommand[] = [];
	devicesCurrentSite: Device[] = [];
	commandsMtmaByDevice: Map<string, string[]> = new Map();
	commandsRbeByDevice: Map<string, string[]> = new Map();
	commandsAutomationsByDevice: Map<string, { iconName: string; commands: string[] }> = new Map();
	rbeOthersOverlayItems: string[] = [];
	mtmaOthersOverlayItems: string[] = [];
	automationsOthersOverlayItems: string[] = [];

	actionVisible: boolean;

	languages = LANGUAGES;
	flagImagePath = './assets/images/flags/';
	flagImageFormat = '.svg';

	oldStr: string;
	private hasScrolledOnDataLoad = false; // prevent double scroll on data load

	// design
	screenWidth: number;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		private elementRef: ElementRef, // used to scroll the dialog content to top
		public config: DynamicDialogConfig
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.permittedCommand$ = this.store.select(selectPermittedCommands);
		this.permission$ = this.store.select(selectPermission);
		this.site$ = this.store.select(selectCurrentSite);
	}

	ngOnInit(): void {
		if (this.config.data.permission) {
			this.permission = { ...this.config.data.permission };
			// we recall GET api for have updated infos
			this.store.dispatch(PermissionActions.getPermission({ permissionId: this.permission.Id }));
		} else {
			this.permission = new Permission();
		}

		this.subscribeAll();
		this.onResize();
	}

	// called after the view has been initialized
	ngAfterViewInit(): void {
		// Keep dialog content at top: default focus would land on CLOSE
		setTimeout(() => this.scrollDialogContentToTop());
	}

	ngOnDestroy(): void {
		this.permissionSub?.unsubscribe();
		this.permittedCommandSub?.unsubscribe();
		this.siteSub?.unsubscribe();
	}

	subscribeAll(): void {
		this.permissionSub = this.permission$?.pipe(filter(p => !!p))
			.subscribe(p => {
				this.permission = p;
				this.buildPermittedCommandsByDevice();
				// scroll the dialog content to top only once on data load
				if (!this.hasScrolledOnDataLoad) {
					this.hasScrolledOnDataLoad = true;
					setTimeout(() => this.scrollDialogContentToTop());
				}
			}
		);
		this.permittedCommandSub = this.permittedCommand$.subscribe(permittedCommands => {
			this.permittedCommands = permittedCommands;
		});
		this.siteSub = this.site$.subscribe(site => {
			if (site?.Devices) {
				this.devicesCurrentSite = site.Devices;
				this.buildPermittedCommandsByDevice();
			}
		});
	}

	private buildPermittedCommandsByDevice(): void {
		this.buildMtmaPermittedCommandsByDevice();
		this.buildRbePermittedCommandsByDevice();
		this.buildAutomationsPermittedCommandsByDevice();
	}

	buildMtmaPermittedCommandsByDevice(): void {
		const commandsByDevice = new Map<string, string[]>();

		if (!this.permission?.Commands?.length || !this.devicesCurrentSite?.length) {
			this.commandsMtmaByDevice = commandsByDevice;
			return;
		}

		const multiIoCommands = this.permission.Commands.filter(cmd => isMultiIo(cmd.ModelId));

		for (const cmd of multiIoCommands) {
			const multiIoDevice = this.devicesCurrentSite.find(dev => dev.Id === cmd.DeviceId);
			if (!multiIoDevice?.ParentId) {
				continue;
			}

			const parentMtma = this.devicesCurrentSite.find(dev =>
				dev.Id === multiIoDevice.ParentId && isMtmacModel(dev.ModelId)
			);
			if (!parentMtma) {
				continue;
			}

			const deviceName = cmd.DeviceName;
			const commandDescriptions = commandsByDevice.get(deviceName) ?? [];
			commandDescriptions.push(cmd.CommandDescription);
			commandsByDevice.set(deviceName, commandDescriptions);
		}

		this.commandsMtmaByDevice = commandsByDevice;
	}

	buildRbePermittedCommandsByDevice(): void {
		const commandsByDevice = new Map<string, string[]>();

		if (!this.permission?.Commands?.length) {
			this.commandsRbeByDevice = commandsByDevice;
			return;
		}

		const rbeCommands = this.permission.Commands.filter(cmd => isRbe24230(cmd.ModelId));

		for (const cmd of rbeCommands) {
			const deviceName = cmd.DeviceName;
			const commandDescriptions = commandsByDevice.get(deviceName) ?? [];
			commandDescriptions.push(cmd.CommandDescription);
			commandsByDevice.set(deviceName, commandDescriptions);
		}

		this.commandsRbeByDevice = commandsByDevice;
	}

	buildAutomationsPermittedCommandsByDevice(): void {
		const commandsByDevice = new Map<string, { iconName: string; commands: string[] }>();

		if (!this.permission?.Commands?.length) {
			this.commandsAutomationsByDevice = commandsByDevice;
			return;
		}

		const automationCommands = this.permission.Commands.filter(cmd =>
			!this.isMappedAsRbeCommand(cmd) && !this.isMappedAsMtmaCommand(cmd)
		);

		for (const cmd of automationCommands) {
			const deviceName = cmd.DeviceName;
			let icon = this.getIcon(cmd.ModelId);
			console.log('icon', icon);
			if (icon === 'cm-undefined') {
				const device = this.devicesCurrentSite.find(dev => dev.Id === cmd.DeviceId);
				if (device) {
					icon = this.generateIcon(device, icon);
				}
			}
			const deviceGroup = commandsByDevice.get(deviceName) ?? {
				iconName: icon,
				commands: []
			};
			deviceGroup.commands.push(cmd.CommandDescription);
			commandsByDevice.set(deviceName, deviceGroup);
		}

		this.commandsAutomationsByDevice = commandsByDevice;
	}

	private isMappedAsRbeCommand(cmd: PermittedCommand): boolean {
		return isRbe24230(cmd.ModelId);
	}

	private isMappedAsMtmaCommand(cmd: PermittedCommand): boolean {
		if (!isMultiIo(cmd.ModelId) || !this.devicesCurrentSite?.length) {
			return false;
		}

		const multiIoDevice = this.devicesCurrentSite.find(dev => dev.Id === cmd.DeviceId);
		if (!multiIoDevice?.ParentId) {
			return false;
		}

		return this.devicesCurrentSite.some(dev =>
			dev.Id === multiIoDevice.ParentId && isMtmacModel(dev.ModelId)
		);
	}

	setFirstDeviceName(cmdDevName: string): string {
		this.oldStr = cmdDevName;
		return cmdDevName;
	}

	getGroup(cmd): boolean {
		if (cmd.DeviceName !== this.oldStr) {
			this.oldStr = cmd.DeviceName;
			return true;
		} else {
			return false;
		}
	}

	getTime(date: Date): string {
		return moment(date).format('HH:mm');
	}

	getLanguage(code: Lang["code"]): string {
		let langName = '';
		LANGUAGES.forEach(lang => {
			if (code === lang.code) {
				langName = lang.name;
			}
		});
		return langName;
	}

	getColorBanner(status: string): string {
		switch (status) {
			case 'expired':
				return 'bg-red-200';
			case 'active':
				return 'bg-green-200';
			case 'ready':
				return 'bg-yellow-200';
			default:
				return 'bg-blue-200';
		}
	}

	getColorStatus(status: string): string {
		switch (status) {
			case 'expired':
				return 'danger';
			case 'active':
				return 'success';
			case 'ready':
				return 'warning';
			default:
				return 'info';
		}
	}

	getCurrentLanguageFlag(customerLanguage): string {
		const currentLangFlagFile = this.flagImagePath + customerLanguage + this.flagImageFormat;
		return currentLangFlagFile;
	}

	getIcon(modelId: number): string {
		let icon = '';
		if (modelId !== EDeviceModel.MultiIO) {
			icon = 'cm-' + DeviceIcon[modelId];
		} else {
			icon = 'cm-' + DeviceIcon[modelId] || 'cm-device-gateway';
		}
		return icon;
	}

	generateIcon(device: Device, icon: string): string {
		switch (icon) {
			case 'cm-undefined': {
				console.log('device', device);
				return convertDbStandartIcon(device.IconName);
			}
			default: {
				return convertOldIcon(icon);
			}
		}
	}

	closeDialog(): void {
		this.ref.close();
	}

	// scroll the dialog content to top
	private scrollDialogContentToTop(): void {
		const dialogContent = this.elementRef.nativeElement.closest('.p-dialog-content') as HTMLElement | null;
		if (dialogContent) {
			dialogContent.scrollTop = 0;
		}
	}

	openRbeOthersOverlay(event: Event, remainingCommands: string[], overlay: OverlayPanel): void {
		this.rbeOthersOverlayItems = remainingCommands;
		overlay.toggle(event);
	}

	openMtmaOthersOverlay(event: Event, remainingCommands: string[], overlay: OverlayPanel): void {
		this.mtmaOthersOverlayItems = remainingCommands;
		overlay.toggle(event);
	}

	openAutomationsOthersOverlay(event: Event, remainingCommands: string[], overlay: OverlayPanel): void {
		this.automationsOthersOverlayItems = remainingCommands;
		overlay.toggle(event);
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-20rem');
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

	col6Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}
}
